import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowDownLeft, ArrowUpRight, ExternalLink, Flag } from "lucide-react";
import { CorrectDialog } from "./CorrectDialog";
import { Chip, Evidence, Facts, FitBadge, label } from "./bits";

export function JudgmentRow({ j, onWrong }) {
  const [open, setOpen] = useState(false);
  const pursuit = j.pursuit?.type !== "none" && j.pursuit?.id ? j.pursuit : null;
  return (
    <Card className={`p-3 ${j.status === "failed" ? "border-rose-200" : ""}`} data-testid={`bd-judgment-${j.judgment_id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 pt-0.5">{j.direction === "out" ? <ArrowUpRight className="w-4 h-4 text-sky-600" /> : <ArrowDownLeft className="w-4 h-4 text-emerald-600" />}</div>
        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen(!open)} data-testid={`bd-judgment-toggle-${j.judgment_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400">{fmtDate(j.date)}</span>
            <span className="text-xs text-slate-600 truncate">{j.direction === "out" ? `to ${(j.to || "").split(",")[0]}` : j.from_email}</span>
            {j.status === "failed" ? <Chip>could not judge</Chip> : <>
              <Chip testid={`bd-judgment-category-${j.judgment_id}`}>{label(j.category)}</Chip>
              <Chip testid={`bd-judgment-event-${j.judgment_id}`}>{label(j.event)}</Chip>
              <FitBadge fit={j.fit} testid={`bd-judgment-fit-${j.judgment_id}`} />
              {j.corrected && <span className="text-[10px] text-amber-700">corrected</span>}
              {j.prefilter_rule && <span className="text-[10px] text-slate-400">rule: {j.prefilter_rule}</span>}
            </>}
          </div>
          <div className="text-sm font-medium text-black truncate mt-0.5">{j.subject || "(no subject)"}</div>
          <div className="text-xs text-slate-600 truncate" data-testid={`bd-judgment-summary-${j.judgment_id}`}>{j.status === "failed" ? j.error : j.summary}</div>
          {pursuit && <div className="text-xs text-[#003E87] mt-0.5">→ {pursuit.type} {pursuit.name || pursuit.id} <span className="text-slate-400">({pursuit.method})</span></div>}
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {j.confidence != null && j.status !== "failed" && <span className="text-[11px] text-slate-400">{Math.round(j.confidence * 100)}%</span>}
          {j.gmail_link && <a href={j.gmail_link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#003E87]" title="Open email" data-testid={`bd-judgment-email-${j.judgment_id}`}><ExternalLink className="w-3.5 h-3.5" /></a>}
          {j.status !== "failed" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onWrong(j)} data-testid={`bd-judgment-wrong-${j.judgment_id}`}><Flag className="w-3 h-3 mr-1" /> Wrong</Button>}
        </div>
      </div>
      {open && j.status !== "failed" && (
        <div className="mt-3 pl-7 text-xs space-y-1" data-testid={`bd-judgment-detail-${j.judgment_id}`}>
          <div><span className="text-slate-400">Fit reason:</span> {j.fit_reason || "—"}</div>
          {j.asks && <div><span className="text-slate-400">Ask:</span> {j.asks}</div>}
          {(j.owed?.we_owe || j.owed?.they_owe) && <div><span className="text-slate-400">Owed:</span> {j.owed.we_owe ? `we owe ${j.owed.we_owe}` : ""}{j.owed.we_owe && j.owed.they_owe ? " · " : ""}{j.owed.they_owe ? `they owe ${j.owed.they_owe}` : ""}</div>}
          {j.next_step && <div><span className="text-slate-400">Next:</span> {j.next_step}</div>}
          {j.pursuit && <div><span className="text-slate-400">Pursuit:</span> {j.pursuit.type === "none" ? "none" : `${j.pursuit.name || j.pursuit.id}`} <span className="text-slate-400">— {j.pursuit.reason}</span></div>}
          <Facts facts={j.facts} />
          <Evidence items={j.evidence} />
          {j.body_preview && <details className="mt-2"><summary className="cursor-pointer text-slate-500">Email text</summary><pre className="whitespace-pre-wrap font-sans text-slate-700 mt-1 bg-slate-50 rounded p-2">{j.body_preview}</pre></details>}
        </div>
      )}
    </Card>
  );
}

// Section 7 + 10: every judgment, newest first, with the "wrong" button.
export function JudgmentList({ replayId, onChanged }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ fit: "", kind: "", client: "" });
  const [wrong, setWrong] = useState(null);
  const load = () => {
    const params = { limit: 300, ...(replayId ? { replay_id: replayId } : {}) };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/bd/judgments", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [replayId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-wrap items-center">
        <select value={filters.fit} onChange={(e) => setFilters({ ...filters, fit: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="bd-filter-fit">
          <option value="">Any fit</option><option value="qualified">Qualified</option><option value="check_me">Check me</option><option value="screened_out">Screened out</option><option value="not_applicable">Not applicable</option>
        </select>
        <Input value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} placeholder="category or event (e.g. we_sent_price)" className="w-64" data-testid="bd-filter-kind" />
        <Input value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })} placeholder="client / sender" className="w-48" data-testid="bd-filter-client" />
        <Button type="submit" variant="outline" data-testid="bd-filter-apply">Filter</Button>
        {rows && <span className="text-xs text-slate-400">{rows.length} shown</span>}
      </form>
      {rows === null ? <div className="text-sm text-slate-400 py-12 text-center">Loading…</div> : !rows.length ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="bd-judgments-empty">No judgments yet.</Card>
      ) : (
        <div className="space-y-2" data-testid="bd-judgment-list">{rows.map((j) => <JudgmentRow key={j.judgment_id} j={j} onWrong={setWrong} />)}</div>
      )}
      <CorrectDialog judgment={wrong} open={!!wrong} onClose={() => setWrong(null)} onDone={() => { load(); onChanged && onChanged(); }} />
    </div>
  );
}
